import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card, CardProps} from 'antd';
import {FormProps} from 'antd/es/form';
import {useUpdateUserMasterAccountMutation, useUserMasterAccountQuery} from 'api/generated';
import {AnchorLink, Hint, PropertyList, Stack} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, Input, SubmitButton} from 'formik-antd';
import {validateCommaSeparatedEmails} from 'lib/validation';
import React, {useState} from 'react';
import * as yup from 'yup';

const parseEmails = (value: string | undefined): string[] => {
  if (!value) return [];
  return value.split(/\s*,\s*/).filter(Boolean);
};

export const EmailNotifications: React.FC<any> = () => {
  const [isEditing, setEditing] = useState(false);
  const [updateUserMasterAccount] = useUpdateUserMasterAccountMutation();

  const {data, loading} = useUserMasterAccountQuery();
  const masterAccount = data?.userMasterAccount;

  const cardProps: CardProps = {
    size: 'small',
    loading,
    type: 'inner',
    style: {
      marginBottom: 25
    }
  };

  const formProps: FormProps = {
    labelAlign: 'left',
    labelCol: {span: 8},
    wrapperCol: {span: 16},
    colon: false
  };

  if (!masterAccount)
    return (
      <>
        <h2>Email notifications</h2>
        <Card {...cardProps} title="Configurations" />
      </>
    );

  const initialValues: any = {
    id: masterAccount.id,
    webhookEmailsString: masterAccount.webhookEmails ? masterAccount.webhookEmails.join(', ') : '',
    churnedEmailsString: masterAccount.churnedEmails ? masterAccount.churnedEmails.join(', ') : ''
  };

  const validationSchema = yup.object().shape({
    webhookEmailsString: yup
      .string()
      .test('valid-emails', 'Invalid email format', (value) => validateCommaSeparatedEmails(value)),
    churnedEmailsString: yup
      .string()
      .test('valid-emails', 'Invalid email format', (value) => validateCommaSeparatedEmails(value))
  });

  const handleSubmit = async (input: any) => {
    await updateUserMasterAccount({
      variables: {
        input: {
          id: masterAccount.id,
          webhookEmails: parseEmails(input.webhookEmailsString),
          churnedEmails: parseEmails(input.churnedEmailsString)
        }
      }
    });
    setEditing(false);
  };

  const webhookEmailsLabel = (
    <Hint message="Emails to send notifications when a webhook delivery fails">Webhook Emails</Hint>
  );

  const churnedEmailsLabel = (
    <Hint message="Emails to send notifications when a customer churns">Churned Emails</Hint>
  );

  return (
    <>
      <h2>Email notifications</h2>
      <Card
        {...cardProps}
        title="Configurations"
        extra={
          isEditing ? null : (
            <AnchorLink onClick={() => setEditing(true)}>
              <EditOutlined /> Edit data
            </AnchorLink>
          )
        }>
        {isEditing ? (
          <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={validationSchema}>
            <Form {...formProps}>
              <FormItem hasFeedback={false} name="webhookEmailsString" label={webhookEmailsLabel}>
                <Input.TextArea
                  name="webhookEmailsString"
                  placeholder="Please write the emails, separated by commas"
                />
              </FormItem>
              <FormItem hasFeedback={false} name="churnedEmailsString" label={churnedEmailsLabel}>
                <Input.TextArea
                  name="churnedEmailsString"
                  placeholder="Please write the emails, separated by commas"
                />
              </FormItem>
              <FormItem
                wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
                name="submit">
                <Stack>
                  <Button onClick={() => setEditing(false)}>Cancel</Button>
                  <SubmitButton>Submit</SubmitButton>
                </Stack>
              </FormItem>
            </Form>
          </Formik>
        ) : (
          <PropertyList
            properties={[
              {
                label: webhookEmailsLabel,
                value:
                  (masterAccount.webhookEmails ?? []).length > 0
                    ? masterAccount.webhookEmails?.join(', ') // Display with comma and space
                    : null
              },
              {
                label: churnedEmailsLabel,
                value:
                  (masterAccount.churnedEmails ?? []).length > 0
                    ? masterAccount.churnedEmails?.join(', ') // Display with comma and space
                    : null
              }
            ]}
          />
        )}
      </Card>
    </>
  );
};
